import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Copy,
  RefreshCw,
  Webhook,
  FlaskConical,
  ChevronRight,
  ClipboardList,
  Plug,
  Stethoscope,
  Plus,
  Pencil,
  Trash2,
  History,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EXAMPLE_CREATED = {
  event: "pix_created",
  external_id: "123",
  name: "João Silva",
  phone: "5599999999999",
  email: "joao@email.com",
  amount: 29.9,
  product: "Plano Mensal",
  pix: "00020126…",
  payment_link: "https://checkout.com/pay/123",
};

const EXAMPLE_PAID = {
  event: "pix_paid",
  external_id: "123",
};

function formatBRL(n) {
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(n || 0));
  } catch {
    return `R$ ${n}`;
  }
}

function statusLabel(s) {
  const m = {
    received: "recebido",
    processed: "processado",
    ignored: "ignorado",
    failed: "falhou",
    duplicate: "duplicado",
    enriched: "enriquecido (API)",
  };
  return m[s] || s || "—";
}

/** Rótulo PT para badge de status do conector (API); a UI aplica `uppercase`. */
function integrationConnectorStatusLabel(raw) {
  const s = (raw || "").toLowerCase();
  const m = { active: "Ativo", error: "Erro", pending: "Pendente", paused: "Pausado" };
  return m[s] || raw || "—";
}

const EVENT_FILTER_LABEL = {
  all: "Todos",
  processed: "Processados",
  ignored: "Ignorados",
  failed: "Falhou",
  missing_phone: "Sem telefone",
  pending: "Pendentes",
  paid: "Pagos",
  recovery_started: "Recuperação iniciada",
};

export default function Integrations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const eventsQs = searchParams.get("events");
  const tabQs = searchParams.get("tab") || (searchParams.get("events") ? "events" : "webhook_api");
  const eventFilter = useMemo(() => {
    if (
      ["ignored", "missing_phone", "processed", "failed", "all", "pending", "paid", "recovery_started"].includes(eventsQs)
    )
      return eventsQs;
    return "all";
  }, [eventsQs]);
  const [mainTab, setMainTab] = useState(tabQs);

  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [uiStatus, setUiStatus] = useState("awaiting_event");
  const [events, setEvents] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [apiConnectors, setApiConnectors] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizStep, setWizStep] = useState(0);
  const [wizName, setWizName] = useState("");
  const [wizAuth, setWizAuth] = useState("api_key");
  const [wizBaseUrl, setWizBaseUrl] = useState("");
  const [wizApiKey, setWizApiKey] = useState("");
  const [wizHeaderName, setWizHeaderName] = useState("");
  const [wizBearer, setWizBearer] = useState("");
  const [wizOAuthUrl, setWizOAuthUrl] = useState("");
  const [wizOAuthId, setWizOAuthId] = useState("");
  const [wizOAuthSecret, setWizOAuthSecret] = useState("");
  const [wizOAuthScope, setWizOAuthScope] = useState("");
  const [wizMethod, setWizMethod] = useState("GET");
  const [wizPath, setWizPath] = useState("/orders/{id}");
  const [wizSaving, setWizSaving] = useState(false);
  const [wizCreated, setWizCreated] = useState(null);
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [diagId, setDiagId] = useState(null);
  const [testConnId, setTestConnId] = useState(null);
  const [testConnOrder, setTestConnOrder] = useState("");
  const [testConnResult, setTestConnResult] = useState(null);
  const [caktoOpen, setCaktoOpen] = useState(false);
  const [caktoName, setCaktoName] = useState("");
  const [caktoClientId, setCaktoClientId] = useState("");
  const [caktoSecret, setCaktoSecret] = useState("");
  const [caktoSaving, setCaktoSaving] = useState(false);
  const [syncpayOpen, setSyncpayOpen] = useState(false);
  const [syncpayName, setSyncpayName] = useState("");
  const [syncpayClientId, setSyncpayClientId] = useState("");
  const [syncpaySecret, setSyncpaySecret] = useState("");
  const [syncpaySaving, setSyncpaySaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCaktoClientId, setEditCaktoClientId] = useState("");
  const [editSecret, setEditSecret] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [diagTabIntegrationId, setDiagTabIntegrationId] = useState("");
  const [diagTabData, setDiagTabData] = useState(null);
  const [diagTabLoading, setDiagTabLoading] = useState(false);
  const [testOrderOpen, setTestOrderOpen] = useState(false);
  const [testOrderIntegId, setTestOrderIntegId] = useState(null);
  const [testOrderInput, setTestOrderInput] = useState("");
  const [testOrderResult, setTestOrderResult] = useState(null);
  const [testOrderRecovering, setTestOrderRecovering] = useState(false);
  const [testOrderRecoveryMsg, setTestOrderRecoveryMsg] = useState(null);

  const [histOpen, setHistOpen] = useState(false);
  const [histIntegId, setHistIntegId] = useState(null);
  const [histIntegName, setHistIntegName] = useState("");
  const [histEvents, setHistEvents] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histFilter, setHistFilter] = useState("all");
  const [regenerateWebhookConfirmId, setRegenerateWebhookConfirmId] = useState(null);

  useEffect(() => {
    const t = searchParams.get("tab");
    const valid = ["webhook_api", "json_direct", "events", "diagnostics"];
    if (t && valid.includes(t)) setMainTab(t);
    else if (searchParams.get("events") && !t) setMainTab("events");
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const evParams = new URLSearchParams({ limit: "50" });
      if (mainTab === "events") {
        if (eventFilter === "processed") evParams.set("event_group", "processed");
        else if (eventFilter === "ignored") evParams.set("event_group", "ignored");
        else if (eventFilter === "failed") evParams.set("event_group", "failed");
        else if (eventFilter === "missing_phone") evParams.set("event_group", "missing_phone");
        else if (eventFilter === "pending") evParams.set("event_group", "pending");
        else if (eventFilter === "paid") evParams.set("event_group", "paid");
        else if (eventFilter === "recovery_started") evParams.set("event_group", "recovery_started");
      }
      const [intRes, evRes, connRes] = await Promise.all([
        api.get("/integrations/custom"),
        api.get(`/integrations/events?${evParams.toString()}`),
        api.get("/integrations/api-connectors").catch(() => ({ data: { integrations: [] } })),
      ]);
      setIntegration(intRes.data.integration);
      setWebhookUrl(intRes.data.webhook_url);
      setUiStatus(intRes.data.ui_status || "awaiting_event");
      setEvents(evRes.data.events || []);
      setApiConnectors(connRes.data?.integrations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível carregar integrações");
    } finally {
      setLoading(false);
    }
  }, [eventFilter, mainTab]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!diagTabIntegrationId && apiConnectors.length > 0) {
      setDiagTabIntegrationId(apiConnectors[0].id);
    }
  }, [apiConnectors, diagTabIntegrationId]);

  const changeMainTab = (v) => {
    setMainTab(v);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", v);
        return p;
      },
      { replace: true },
    );
  };

  const setEventFilter = (key) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", "events");
        if (key === "all") p.delete("events");
        else p.set("events", key);
        return p;
      },
      { replace: true },
    );
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copiado!");
  };

  const regenerate = async () => {
    const { data } = await api.post("/integrations/custom/regenerate-token");
    setIntegration(data.integration);
    setWebhookUrl(data.webhook_url);
    toast.success("Novo token de webhook gerado");
  };

  const openDetail = async (ev) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const { data } = await api.get(`/integrations/events/${ev.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar evento");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const runTest = async () => {
    setTestResult(null);
    try {
      const { data } = await api.post("/integrations/custom/test", { test_phone: testPhone || null });
      setTestResult(data);
      if (data.success && data.status === "processed") toast.success("Teste concluído — evento processado");
      else if (data.success && data.status === "ignored") toast.message("Teste recebido", { description: data.reason || "ignorado" });
      else toast.error(data.error || "Falha no teste");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Erro no teste");
    }
  };

  const openWizard = () => {
    setWizStep(0);
    setWizName("");
    setWizAuth("api_key");
    setWizBaseUrl("");
    setWizApiKey("");
    setWizHeaderName("");
    setWizBearer("");
    setWizOAuthUrl("");
    setWizOAuthId("");
    setWizOAuthSecret("");
    setWizOAuthScope("");
    setWizMethod("GET");
    setWizPath("/orders/{id}");
    setWizCreated(null);
    setWizardOpen(true);
  };

  const wizCredentialsPayload = () => {
    if (wizAuth === "api_key") {
      const o = { api_key: wizApiKey };
      if (wizHeaderName.trim()) o.header_name = wizHeaderName.trim();
      return o;
    }
    if (wizAuth === "bearer_token") return { access_token: wizBearer };
    return {
      client_id: wizOAuthId,
      client_secret: wizOAuthSecret,
      token_url: wizOAuthUrl,
      scope: wizOAuthScope || undefined,
    };
  };

  const submitWizard = async () => {
    setWizSaving(true);
    try {
      const { data } = await api.post("/integrations/api-connectors", {
        name: wizName.trim(),
        provider: "generic_api",
        auth_type: wizAuth,
        base_url: wizBaseUrl.trim(),
        credentials: wizCredentialsPayload(),
        order_detail_method: wizMethod,
        order_detail_path: wizPath.trim(),
        order_detail_id_source: "external_transaction_id",
      });
      setWizCreated(data.integration);
      setWizStep(4);
      toast.success("Integração criada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Erro ao criar");
    } finally {
      setWizSaving(false);
    }
  };

  const openDiagnostics = async (id) => {
    setDiagId(id);
    setDiagOpen(true);
    setDiagLoading(true);
    setDiagData(null);
    try {
      const { data } = await api.get(`/integrations/api-connectors/${id}/diagnostics`);
      setDiagData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no diagnóstico");
      setDiagOpen(false);
    } finally {
      setDiagLoading(false);
    }
  };

  const runTestConnection = async (id) => {
    setTestConnId(id);
    setTestConnResult(null);
    try {
      const { data } = await api.post(`/integrations/api-connectors/${id}/test-connection`, {
        test_order_id: testConnOrder.trim() || null,
      });
      setTestConnResult(data);
      if (data.ok) toast.success("Teste de conexão concluído");
      else toast.error(data.error || "Falha");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no teste");
    }
  };

  const runTestWebhookConnector = async (id) => {
    try {
      const { data } = await api.post(`/integrations/api-connectors/${id}/test-webhook`, {
        payload: { event: "status_changed", id: `sim_${Date.now()}` },
      });
      if (data.success && data.status === "processed") toast.success("Fluxo: webhook → API → processado");
      else if (data.success && data.status === "ignored")
        toast.message("Evento ignorado", {
          description:
            data.reason === "synthetic_test_order_id"
              ? "O teste interno usa um ID fictício. Use «Testar pedido» com o UUID ou refId de um pedido real na Cakto."
              : data.reason || "",
        });
      else toast.error(data.error || "Falha");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no teste de webhook");
    }
  };

  const submitCakto = async () => {
    if (!caktoName.trim() || !caktoClientId.trim() || !caktoSecret.trim()) {
      toast.error("Preencha nome, Client ID e Client Secret");
      return;
    }
    setCaktoSaving(true);
    try {
      await api.post("/integrations/api-connectors/cakto", {
        name: caktoName.trim(),
        client_id: caktoClientId.trim(),
        client_secret: caktoSecret.trim(),
      });
      toast.success("Integração Cakto criada");
      setCaktoOpen(false);
      setCaktoName("");
      setCaktoClientId("");
      setCaktoSecret("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar integração Cakto");
    } finally {
      setCaktoSaving(false);
    }
  };

  const saveSyncpay = async () => {
    if (!syncpayName.trim() || !syncpayClientId.trim() || !syncpaySecret.trim()) {
      toast.error("Preencha nome, Client ID e Client Secret");
      return;
    }
    setSyncpaySaving(true);
    try {
      await api.post("/integrations/api-connectors/syncpay", {
        name: syncpayName.trim(),
        client_id: syncpayClientId.trim(),
        client_secret: syncpaySecret.trim(),
      });
      toast.success("Integração Sync Pay criada");
      setSyncpayOpen(false);
      setSyncpayName("");
      setSyncpayClientId("");
      setSyncpaySecret("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar integração Sync Pay");
    } finally {
      setSyncpaySaving(false);
    }
  };

  const regenerateConnectorWebhook = async (id) => {
    try {
      await api.post(`/integrations/api-connectors/${id}/regenerate-webhook`);
      toast.success("Nova URL de webhook gerada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao regenerar");
    }
  };

  const removeConnector = async (id) => {
    if (!window.confirm("Remover esta integração? O webhook desta URL deixará de funcionar.")) return;
    try {
      await api.delete(`/integrations/api-connectors/${id}`);
      toast.success("Integração removida");
      if (diagTabIntegrationId === id) {
        setDiagTabIntegrationId("");
        setDiagTabData(null);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remover");
    }
  };

  const submitEditConnector = async () => {
    if (!editRow?.id) return;
    setEditSaving(true);
    try {
      const body = { name: editName.trim() };
      if (
        (editRow.auth_type === "cakto_token" || editRow.auth_type === "syncpay_token") &&
        (editCaktoClientId.trim() || editSecret.trim())
      ) {
        if (!editCaktoClientId.trim() || !editSecret.trim()) {
          toast.error("Para atualizar credenciais, informe Client ID e Client Secret completos");
          return;
        }
        body.credentials = { client_id: editCaktoClientId.trim(), client_secret: editSecret.trim() };
      }
      await api.patch(`/integrations/api-connectors/${editRow.id}`, body);
      toast.success("Integração atualizada");
      setEditOpen(false);
      setEditRow(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar");
    } finally {
      setEditSaving(false);
    }
  };

  const runTestOrderModal = async () => {
    if (!testOrderIntegId || !testOrderInput.trim()) {
      toast.error("Informe o ID do pedido");
      return;
    }
    setTestOrderResult(null);
    setTestOrderRecoveryMsg(null);
    try {
      const { data } = await api.post(`/integrations/api-connectors/${testOrderIntegId}/test-order`, {
        order_id: testOrderInput.trim(),
      });
      setTestOrderResult(data);
      if (data.ok) toast.success("Consulta concluída");
      else toast.error(data.error || "Falha na consulta");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao consultar pedido");
    }
  };

  const recoveryHintFromTestResult = (data) => {
    if (!data?.checks?.length) return false;
    const row = data.checks.find((c) => (c.label || "").includes("Pronto para recuperação"));
    return !!row?.ok;
  };

  const runStartRecoveryFromTest = async () => {
    if (!testOrderIntegId || !testOrderInput.trim()) {
      toast.error("Informe o ID do pedido");
      return;
    }
    setTestOrderRecovering(true);
    setTestOrderRecoveryMsg(null);
    try {
      const { data } = await api.post(`/integrations/api-connectors/${testOrderIntegId}/test-order/start-recovery`, {
        order_id: testOrderInput.trim(),
      });
      setTestOrderRecoveryMsg(data.message || (data.ok ? "OK" : "Não iniciado"));
      if (data.ok) toast.success(data.message || "Recuperação iniciada");
      else toast.message(data.message || "Recuperação não iniciada", { description: data.result?.reason || data.result?.skip_reason || "" });
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setTestOrderRecoveryMsg(typeof d === "string" ? d : "Erro ao iniciar recuperação");
      toast.error(d || "Erro ao iniciar recuperação");
    } finally {
      setTestOrderRecovering(false);
    }
  };

  const loadIntegrationHistory = async (integrationId, filter) => {
    if (!integrationId) return;
    setHistLoading(true);
    try {
      const p = new URLSearchParams({ limit: "60", integration_id: integrationId });
      if (filter === "processed") p.set("event_group", "processed");
      else if (filter === "ignored") p.set("event_group", "ignored");
      else if (filter === "failed") p.set("event_group", "failed");
      else if (filter === "missing_phone") p.set("event_group", "missing_phone");
      else if (filter === "pending") p.set("event_group", "pending");
      else if (filter === "paid") p.set("event_group", "paid");
      else if (filter === "recovery_started") p.set("event_group", "recovery_started");
      const { data } = await api.get(`/integrations/events?${p.toString()}`);
      setHistEvents(data.events || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar histórico");
      setHistEvents([]);
    } finally {
      setHistLoading(false);
    }
  };

  const openIntegrationHistory = (c) => {
    setHistIntegId(c.id);
    setHistIntegName(c.name || c.id);
    setHistFilter("all");
    setHistOpen(true);
    loadIntegrationHistory(c.id, "all");
  };

  const loadDiagTab = async () => {
    if (!diagTabIntegrationId) {
      toast.message("Selecione uma integração");
      return;
    }
    setDiagTabLoading(true);
    setDiagTabData(null);
    try {
      const { data } = await api.get(`/integrations/api-connectors/${diagTabIntegrationId}/diagnostics`);
      setDiagTabData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no diagnóstico");
    } finally {
      setDiagTabLoading(false);
    }
  };

  const openEditConnector = (c) => {
    setEditRow(c);
    setEditName(c.name || "");
    setEditCaktoClientId("");
    setEditSecret("");
    setEditOpen(true);
  };

  const testOrderIntegration = useMemo(
    () => apiConnectors.find((x) => x.id === testOrderIntegId),
    [apiConnectors, testOrderIntegId],
  );
  const testOrderIdPlaceholder =
    (testOrderIntegration?.provider || testOrderIntegration?.platform || "").toLowerCase() === "syncpay"
      ? "UUID da transação (data.id do webhook cashin)"
      : "ID do pedido na plataforma";

  const uiStatusText =
    uiStatus === "awaiting_event"
      ? "Aguardando primeiro evento"
      : uiStatus === "integrated"
        ? "Integrado"
        : uiStatus === "error"
          ? "Com erro"
          : uiStatus;

  const checklist = testResult
    ? [
        { ok: !!testResult.correlation_id, label: "Evento recebido" },
        { ok: testResult.status !== "failed" && testResult.reason !== "missing_external_id", label: "Cliente / referência identificados" },
        { ok: testResult.reason !== "missing_phone", label: "Telefone válido" },
        { ok: testResult.status === "processed" && !!testResult.transaction_id, label: "Transação criada" },
        {
          ok: testResult.recovery_started === true,
          label:
            testResult.recovery_started === true
              ? "Recuperação iniciada / agendada"
              : testResult.skip_reason === "whatsapp_disconnected" || testResult.reason === "whatsapp_disconnected"
                ? "Recuperação não iniciada (WhatsApp desconectado)"
                : testResult.skip_reason === "recovery_disabled" || testResult.reason === "recovery_disabled"
                  ? "Recuperação não iniciada (recuperação desligada)"
                  : testResult.status === "processed"
                    ? "Recuperação não iniciada (ver regras)"
                    : "—",
        },
      ]
    : [];

  if (loading && !integration) {
    return <div className="text-slate-500 font-mono text-sm">carregando…</div>;
  }

  const btnPrimary =
    "inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-sm border border-cp-primary/45 bg-cp-primary/15 text-cp-primary-light hover:bg-cp-primary/25 transition-colors";
  const btnSecondary =
    "inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm border border-white/[0.12] text-slate-300 hover:bg-white/[0.04] hover:border-white/20 transition-colors";
  const btnDanger =
    "inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm border border-red-500/25 text-red-300 hover:bg-red-500/10 transition-colors";
  const btnSuccess =
    "inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-sm border border-emerald-500/40 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25 hover:border-emerald-500/55 transition-colors";

  return (
    <div className="space-y-8" data-testid="integrations-page">
      <header className="space-y-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-cp-primary-light">_integrações</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter text-slate-50">Integrações</h1>
        <p className="text-slate-400 text-sm max-w-2xl leading-relaxed">
          Conecte seu checkout para a ConvertPix receber eventos e consultar os dados da venda automaticamente.
        </p>
      </header>

      <Tabs value={mainTab} onValueChange={changeMainTab} className="w-full">
        <TabsList className="mb-5 flex h-auto w-full flex-wrap justify-start gap-1 rounded-sm border border-white/[0.08] bg-cp-elevated/50 p-1.5 shadow-inner">
          <TabsTrigger
            value="webhook_api"
            className="shrink-0 rounded-sm px-3 py-2 text-[11px] font-medium text-slate-500 transition-all hover:text-slate-300 data-[state=active]:border data-[state=active]:border-cp-primary/50 data-[state=active]:bg-cp-primary/20 data-[state=active]:text-cp-primary-light data-[state=active]:shadow-sm"
          >
            Webhook + API
          </TabsTrigger>
          <TabsTrigger
            value="json_direct"
            className="shrink-0 rounded-sm px-3 py-2 text-[11px] font-medium text-slate-500 transition-all hover:text-slate-300 data-[state=active]:border data-[state=active]:border-cp-primary/50 data-[state=active]:bg-cp-primary/20 data-[state=active]:text-cp-primary-light data-[state=active]:shadow-sm"
          >
            Webhook JSON direto
          </TabsTrigger>
          <TabsTrigger
            value="events"
            className="shrink-0 rounded-sm px-3 py-2 text-[11px] font-medium text-slate-500 transition-all hover:text-slate-300 data-[state=active]:border data-[state=active]:border-cp-primary/50 data-[state=active]:bg-cp-primary/20 data-[state=active]:text-cp-primary-light data-[state=active]:shadow-sm"
          >
            Últimos eventos
          </TabsTrigger>
          <TabsTrigger
            value="diagnostics"
            className="shrink-0 rounded-sm px-3 py-2 text-[11px] font-medium text-slate-500 transition-all hover:text-slate-300 data-[state=active]:border data-[state=active]:border-cp-primary/50 data-[state=active]:bg-cp-primary/20 data-[state=active]:text-cp-primary-light data-[state=active]:shadow-sm"
          >
            Diagnóstico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="webhook_api" className="mt-0 space-y-6">
          <section className="rounded-sm border border-white/[0.08] bg-cp-bg/80 px-5 py-5 md:px-6 md:py-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
              <div className="min-w-0 flex-1 space-y-1.5">
                <h2 className="font-heading text-lg font-semibold text-slate-100">Integração via API</h2>
                <p className="text-xs text-slate-500 leading-relaxed max-w-xl">
                  O webhook avisa mudanças; em seguida consultamos a API para nome, telefone, valor e status. Use Cakto, Sync Pay ou configure outra plataforma.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2 lg:justify-end lg:pt-0.5">
                <button
                  type="button"
                  onClick={() => {
                    setCaktoName("");
                    setCaktoClientId("");
                    setCaktoSecret("");
                    setCaktoOpen(true);
                  }}
                  className="inline-flex items-center gap-2 rounded-sm border border-cp-primary/50 bg-cp-primary/25 px-4 py-2.5 text-sm font-medium text-cp-primary-light hover:bg-cp-primary/35"
                >
                  <Plug className="h-4 w-4 shrink-0" />
                  Conectar Cakto
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSyncpayName("");
                    setSyncpayClientId("");
                    setSyncpaySecret("");
                    setSyncpayOpen(true);
                  }}
                  className="inline-flex items-center gap-2 rounded-sm border border-cp-primary/50 bg-cp-primary/25 px-4 py-2.5 text-sm font-medium text-cp-primary-light hover:bg-cp-primary/35"
                >
                  <Plug className="h-4 w-4 shrink-0" />
                  Conectar Sync Pay
                </button>
                <button
                  type="button"
                  onClick={openWizard}
                  className="inline-flex items-center gap-2 rounded-sm border border-cp-primary/35 bg-cp-primary/10 px-4 py-2.5 text-sm font-medium text-cp-primary-light hover:bg-cp-primary/20"
                >
                  <Plus className="h-4 w-4 shrink-0" />
                  Nova integração
                </button>
              </div>
            </div>

            {apiConnectors.length === 0 ? (
              <p className="mt-5 border-t border-white/[0.06] pt-5 text-sm text-slate-500">
                Nenhuma integração via API ainda. Use &quot;Conectar Cakto&quot;, &quot;Conectar Sync Pay&quot; ou &quot;Nova integração&quot; para gerar uma URL de webhook
                dedicada.
              </p>
            ) : (
              <div className="mt-6 space-y-4 border-t border-white/[0.06] pt-6">
                {apiConnectors.map((c) => {
                  const st = (c.status || "").toLowerCase();
                  const statusRing =
                    st === "active"
                      ? "border-emerald-500/45 bg-emerald-500/10 text-emerald-200"
                      : st === "error"
                        ? "border-red-500/45 bg-red-500/10 text-red-200"
                        : st === "pending"
                          ? "border-amber-500/45 bg-amber-500/10 text-amber-100"
                          : "border-white/12 bg-white/[0.03] text-slate-400";
                  const authLabel =
                    c.auth_type === "cakto_token"
                      ? "Client ID + Secret (Cakto)"
                      : c.auth_type === "syncpay_token"
                        ? "Client ID + Secret (Sync Pay)"
                        : c.auth_type === "oauth2_client_credentials"
                        ? "OAuth2 (client credentials)"
                        : c.auth_type === "bearer_token"
                          ? "Bearer token"
                          : c.auth_type === "api_key"
                            ? "Chave de API"
                            : c.auth_type || "—";
                  const plat = (c.provider || c.platform || "api").toString();
                  return (
                    <article
                      key={c.id}
                      className="space-y-5 rounded-sm border border-white/[0.08] bg-cp-elevated/25 p-5 md:p-6"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
                        <div className="min-w-0 flex-1 space-y-3">
                          <div className="flex flex-wrap items-center gap-2 gap-y-2">
                            <h3 className="font-heading text-lg font-semibold leading-tight text-slate-50 md:text-xl">{c.name}</h3>
                            <span className="rounded-sm border border-white/12 bg-cp-bg/50 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                              {plat}
                            </span>
                            <span className={`rounded-sm border px-2 py-0.5 font-mono text-[10px] font-semibold uppercase ${statusRing}`}>
                              {integrationConnectorStatusLabel(c.status)}
                            </span>
                          </div>
                          <dl className="flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11px] text-slate-500">
                            <div>
                              <dt className="text-[10px] uppercase text-slate-600">Último evento</dt>
                              <dd className="mt-0.5 text-slate-400">{c.last_event_at || "—"}</dd>
                            </div>
                            <div>
                              <dt className="text-[10px] uppercase text-slate-600">Última consulta API</dt>
                              <dd className="mt-0.5 text-slate-400">{c.last_api_query_at || c.last_sync_at || "—"}</dd>
                            </div>
                          </dl>
                        </div>
                      </div>

                      <div className="grid gap-4 border-t border-white/[0.06] pt-4 sm:grid-cols-2">
                        <div>
                          <div className="text-[10px] font-mono uppercase text-slate-600">Autenticação</div>
                          <p className="mt-1 text-xs text-slate-300">{authLabel}</p>
                        </div>
                        <div>
                          <div className="text-[10px] font-mono uppercase text-slate-600">Tipo</div>
                          <p className="mt-1 font-mono text-xs uppercase text-slate-400">{plat}</p>
                        </div>
                      </div>

                      {c.last_error_message ? (
                        <div className="rounded-sm border border-red-500/25 bg-red-500/[0.06] px-3 py-2 text-xs text-red-200/95">
                          Erro: {c.last_error_message}
                        </div>
                      ) : null}

                      <div className="rounded-sm border border-cp-primary/30 bg-cp-primary/[0.06] p-4">
                        <div className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-wide text-cp-primary-light/90">
                          URL do webhook
                        </div>
                        <div className="flex items-stretch gap-2">
                          <code className="min-h-[2.5rem] flex-1 overflow-x-auto whitespace-nowrap rounded-sm border border-cp-primary/35 bg-cp-bg px-3 py-2 font-mono text-[11px] leading-relaxed text-cp-primary-light">
                            {c.webhook_url_token_only || "—"}
                          </code>
                          <button
                            type="button"
                            onClick={() => copy(c.webhook_url_token_only)}
                            className="inline-flex shrink-0 items-center justify-center rounded-sm border border-white/15 bg-cp-elevated/80 px-3 hover:bg-white/10"
                            title="Copiar URL"
                          >
                            <Copy className="h-4 w-4 text-slate-300" />
                          </button>
                        </div>
                        {c.webhook_url_token_only && /localhost|127\.0\.0\.1/i.test(c.webhook_url_token_only) ? (
                          <p className="mt-2 text-[11px] leading-snug text-amber-200/80">
                            Esta URL usa <span className="font-mono">localhost</span>: o gateway externo não consegue alcançá-la pela internet. Use um domínio HTTPS público
                            (deploy ou túnel) e, no servidor, defina <span className="font-mono">PUBLIC_WEBHOOK_BASE_URL</span> para a URL base pública — assim a URL
                            copiada aqui coincide com o que a plataforma deve chamar.
                          </p>
                        ) : null}
                      </div>

                      {plat === "cakto" ? (
                        <aside className="rounded-sm border border-amber-500/15 bg-amber-500/[0.04] px-3 py-2.5 text-[11px] leading-snug text-slate-500">
                          <span className="font-medium text-amber-200/85">Cakto:</span> Apps → Webhooks — use{" "}
                          <strong className="text-slate-400">esta URL</strong>, evento{" "}
                          <span className="font-mono text-cp-primary-light/90">pix_gerado</span> ou outro com{" "}
                          <span className="font-mono text-slate-400">id</span>/<span className="font-mono text-slate-400">refId</span> no JSON.
                          Sem POST para a ConvertPix, o histórico não atualiza ao gerar o PIX.
                        </aside>
                      ) : null}
                      {plat === "syncpay" ? (
                        <aside className="rounded-sm border border-amber-500/15 bg-amber-500/[0.04] px-3 py-2.5 text-[11px] leading-snug text-slate-500">
                          <span className="font-medium text-amber-200/85">Sync Pay — evento do webhook:</span> para PIX de{" "}
                          <strong className="text-slate-300">entrada</strong> (cobrança / quem vai pagar), escolha{" "}
                          <strong className="text-slate-300">Recebimento - Cash in</strong>. O evento{" "}
                          <strong className="text-slate-300">Transação - Cash out</strong>{" "}
                          <span className="text-amber-200/90">não envia dados do pagador</span> (só id, valor, pix, status) — a API{" "}
                          <span className="font-mono text-slate-400">GET /transaction/{"{id}"}</span> também{" "}
                          <span className="text-amber-200/90">não devolve cliente</span> na documentação; nome/telefone vêm do webhook{" "}
                          <strong className="text-slate-300">cash in</strong>. URL: esta integração; id:{" "}
                          <span className="font-mono text-cp-primary-light/90">data.id</span>. Timeout 5s; autorize IP se exigido.
                        </aside>
                      ) : null}

                      <div className="space-y-3 border-t border-white/[0.06] pt-4">
                        <p className="text-[10px] font-mono uppercase tracking-wide text-slate-600">Ações</p>
                        <div className="flex flex-wrap gap-2">
                          <button type="button" onClick={() => openIntegrationHistory(c)} className={btnPrimary}>
                            <History className="h-3.5 w-3.5 shrink-0" />
                            Ver histórico
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setTestOrderIntegId(c.id);
                              setTestOrderInput("");
                              setTestOrderResult(null);
                              setTestOrderRecoveryMsg(null);
                              setTestOrderOpen(true);
                            }}
                            className={btnPrimary}
                          >
                            Testar pedido
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setDiagTabIntegrationId(c.id);
                              changeMainTab("diagnostics");
                            }}
                            className={btnPrimary}
                          >
                            <Stethoscope className="h-3.5 w-3.5 shrink-0" />
                            Ver diagnóstico
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button type="button" onClick={() => openEditConnector(c)} className={btnSecondary}>
                            <Pencil className="h-3.5 w-3.5 shrink-0" />
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => setRegenerateWebhookConfirmId(c.id)}
                            className={btnSecondary}
                            title="Gera nova URL; atualize na plataforma externa"
                          >
                            <RefreshCw className="h-3.5 w-3.5 shrink-0" />
                            Regenerar webhook
                          </button>
                          <button type="button" onClick={() => runTestWebhookConnector(c.id)} className={btnSecondary}>
                            <FlaskConical className="h-3.5 w-3.5 shrink-0" />
                            Testar webhook
                          </button>
                          {testConnId === c.id ? (
                            <div className="w-full basis-full space-y-4 rounded-sm border border-white/[0.08] bg-cp-bg/40 p-3">
                              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">
                                <input
                                  value={testConnOrder}
                                  onChange={(e) => setTestConnOrder(e.target.value)}
                                  placeholder="ID de pedido de teste (opcional, para teste de conexão)"
                                  className="min-w-0 w-full max-w-full rounded-sm border border-white/10 bg-cp-bg px-2 py-2 font-mono text-xs sm:max-w-xs"
                                />
                                <button
                                  type="button"
                                  onClick={() => runTestConnection(c.id)}
                                  className={`${btnSuccess} shrink-0`}
                                >
                                  Executar teste de conexão
                                </button>
                              </div>
                              {testConnResult ? (
                                <ul className="space-y-1 text-xs text-slate-400">
                                  {(testConnResult.result?.checks || testConnResult.checks || []).map((ch, i) => (
                                    <li key={i} className={ch.ok ? "text-emerald-400" : "text-amber-300"}>
                                      {ch.ok ? "✅" : "⚠️"} {ch.label}
                                      {ch.detail ? ` — ${ch.detail}` : ""}
                                    </li>
                                  ))}
                                </ul>
                              ) : null}
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setTestConnId(c.id);
                                setTestConnOrder("");
                                setTestConnResult(null);
                              }}
                              className={btnSecondary}
                            >
                              Testar conexão com a API…
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 border-t border-white/[0.05] pt-3">
                          <button type="button" onClick={() => removeConnector(c.id)} className={btnDanger}>
                            <Trash2 className="h-3.5 w-3.5 shrink-0" />
                            Remover
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </TabsContent>

        <TabsContent value="json_direct" className="mt-0 space-y-6">
          <section className="rounded-sm border border-white/[0.08] bg-cp-bg/80 px-5 py-5 md:px-6 md:py-6">
            <h2 className="mb-3 font-heading text-lg font-semibold text-slate-100">Como usar (JSON completo no webhook)</h2>
            <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-slate-300">
              <li>Copie a URL do webhook personalizado abaixo.</li>
              <li>Cole no seu checkout ou gateway (área de notificações / webhooks).</li>
              <li>Configure os avisos de PIX gerado e PIX pago, se o sistema permitir escolher eventos.</li>
              <li>Use &quot;Enviar evento teste&quot; para validar tudo por aqui.</li>
              <li>Confira se o evento apareceu na lista — pronto para produção.</li>
            </ol>
            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              Guia completo (link rastreável, pixel, mapeamento de payload e checklist):{" "}
              <code className="text-cp-primary-light">docs/INTEGRATION_PLAYBOOK.md</code> no repositório do projeto.
            </p>
          </section>

          <section className="rounded-sm border border-white/[0.08] bg-cp-bg/80 px-5 py-5 md:px-6 md:py-6">
            <div className="mb-6 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-cp-primary/30 bg-cp-primary/10">
                <Webhook className="h-5 w-5 text-cp-primary-light" />
              </div>
              <div className="min-w-0">
                <div className="font-heading text-xl font-semibold text-slate-100">Webhook personalizado</div>
                <div className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">POST JSON — URL única por conta</div>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-3">
            <label className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-wide text-slate-600">URL do webhook</label>
            <div className="flex items-stretch gap-2">
              <code
                data-testid="integration-webhook-url"
                className="flex-1 overflow-x-auto whitespace-nowrap rounded-sm border border-cp-primary/35 bg-cp-bg px-3 py-2.5 font-mono text-xs text-cp-primary-light"
              >
                {webhookUrl || "—"}
              </code>
              <button
                type="button"
                onClick={() => webhookUrl && copy(webhookUrl)}
                className="inline-flex shrink-0 items-center justify-center rounded-sm border border-white/15 bg-cp-elevated/80 px-3 transition-all hover:bg-white/10"
                title="Copiar URL"
              >
                <Copy className="h-4 w-4 text-slate-300" />
              </button>
            </div>

            <div className="text-xs text-slate-500">
              Status: <span className="text-slate-200">{uiStatusText}</span>
              {integration?.last_event_at ? (
                <span className="mt-1 block">Último evento: {integration.last_event_at}</span>
              ) : null}
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                onClick={regenerate}
                className="inline-flex items-center gap-2 rounded-sm border border-white/[0.12] px-3 py-2 text-xs text-slate-300 hover:bg-white/[0.04]"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Regenerar token
              </button>
              <button
                type="button"
                onClick={() => copy(JSON.stringify(EXAMPLE_CREATED, null, 2))}
                className="inline-flex items-center gap-2 rounded-sm border border-white/[0.12] px-3 py-2 text-xs text-slate-300 hover:bg-white/[0.04]"
              >
                <Copy className="h-3.5 w-3.5" />
                Copiar exemplo PIX criado
              </button>
            </div>
          </div>

          <div className="space-y-3 rounded-sm border border-white/[0.08] bg-cp-elevated/30 p-4">
            <div className="font-mono text-[10px] font-semibold uppercase tracking-wide text-slate-600">Enviar evento teste</div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Simula um PIX criado (R$ 29,90, produto teste). Opcional: informe um telefone com DDI (ex. 5511999999999) para testar com seu
              número.
            </p>
            <input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="Telefone (opcional)"
              className="w-full bg-cp-bg border border-white/10 rounded-sm px-3 py-2 text-sm font-mono"
            />
            <button
              type="button"
              onClick={runTest}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-cp-primary/20 border border-cp-primary/40 text-cp-primary-light rounded-sm hover:bg-cp-primary/30"
            >
              <FlaskConical className="h-4 w-4" />
              Enviar evento teste
            </button>
            {testResult ? (
              <ul className="mt-3 space-y-1.5 text-xs border-t border-white/10 pt-3">
                {checklist.map((row) => (
                  <li key={row.label} className={row.ok ? "text-emerald-400" : "text-amber-300"}>
                    {row.ok ? "✅" : "⚠️"} {row.label}
                  </li>
                ))}
                <li className="text-slate-500 font-mono mt-2 break-all">correlation_id: {testResult.correlation_id}</li>
              </ul>
            ) : null}
          </div>
        </div>

            <div className="mt-8 border-t border-white/[0.06] pt-6">
              <div className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-wide text-slate-600">Exemplo de payload</div>
              <Tabs defaultValue="created" className="w-full">
                <TabsList className="h-auto gap-1 rounded-sm border border-white/[0.08] bg-cp-elevated/50 p-1">
                  <TabsTrigger
                    value="created"
                    className="rounded-sm px-3 py-1.5 text-[11px] data-[state=active]:border data-[state=active]:border-cp-primary/45 data-[state=active]:bg-cp-primary/15 data-[state=active]:text-cp-primary-light"
                  >
                    PIX criado
                  </TabsTrigger>
                  <TabsTrigger
                    value="paid"
                    className="rounded-sm px-3 py-1.5 text-[11px] data-[state=active]:border data-[state=active]:border-cp-primary/45 data-[state=active]:bg-cp-primary/15 data-[state=active]:text-cp-primary-light"
                  >
                    PIX pago
                  </TabsTrigger>
                </TabsList>
            <TabsContent value="created">
              <pre className="bg-cp-elevated border border-white/10 rounded-sm p-4 font-mono text-xs text-slate-300 overflow-x-auto mt-2">
                {JSON.stringify(EXAMPLE_CREATED, null, 2)}
              </pre>
              <button
                type="button"
                onClick={() => copy(JSON.stringify(EXAMPLE_CREATED, null, 2))}
                className="mt-2 text-xs text-cp-primary-light underline"
              >
                Copiar exemplo
              </button>
              <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                Sem <code className="text-cp-primary-light">external_id</code> no gateway: envie{" "}
                <code className="text-cp-primary-light">cpix_id</code> (o mesmo da URL após o link rastreável). O ConvertPix usa esse valor
                como chave da venda e cruza com a sessão do pixel para obter telefone quando o JSON não trouxer contato.
              </p>
            </TabsContent>
            <TabsContent value="paid">
              <pre className="bg-cp-elevated border border-white/10 rounded-sm p-4 font-mono text-xs text-slate-300 overflow-x-auto mt-2">
                {JSON.stringify(EXAMPLE_PAID, null, 2)}
              </pre>
              <button
                type="button"
                onClick={() => copy(JSON.stringify(EXAMPLE_PAID, null, 2))}
                className="mt-2 text-xs text-cp-primary-light underline"
              >
                Copiar exemplo
              </button>
            </TabsContent>
          </Tabs>
                <p className="mt-3 text-xs text-slate-500">
                  Envie sempre <code className="text-cp-primary-light">Content-Type: application/json</code>. O corpo deve ser um objeto JSON.
                </p>
              </div>
            </section>
        </TabsContent>

        <TabsContent value="events" className="mt-0 space-y-6">
          <section className="rounded-sm border border-white/[0.08] bg-cp-bg/80 px-5 py-5 md:px-6 md:py-6">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="font-heading text-lg font-semibold text-slate-100">Últimos eventos recebidos</h2>
              <div className="flex flex-wrap items-center gap-2">
                {(["all", "processed", "recovery_started", "pending", "paid", "ignored", "missing_phone", "failed"]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setEventFilter(k)}
                    className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border transition ${
                      eventFilter === k
                        ? "bg-cp-primary/25 border-cp-primary/50 text-cp-primary-light"
                        : "border-white/10 text-slate-500 hover:text-slate-300"
                    }`}
                  >
                    {EVENT_FILTER_LABEL[k]}
                  </button>
                ))}
                <button type="button" onClick={load} className="text-xs text-cp-primary-light hover:underline sm:ml-1">
                  Atualizar
                </button>
              </div>
            </div>
            {events.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum evento ainda. Faça um teste ou envie do seu checkout.</p>
            ) : (
              <div className="overflow-x-auto border border-white/[0.06] rounded-sm">
                <table className="w-full text-left text-xs">
                  <thead className="bg-cp-elevated text-slate-500 font-mono uppercase">
                    <tr>
                      <th className="px-3 py-2">Quando</th>
                      <th className="px-3 py-2">Integração</th>
                      <th className="px-3 py-2">Origem</th>
                      <th className="px-3 py-2">Evento</th>
                      <th className="px-3 py-2">Cliente / ref.</th>
                      <th className="px-3 py-2">Valor</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Recuperação</th>
                      <th className="px-3 py-2 w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((ev) => {
                      const norm = ev.normalized_payload || {};
                      const cust = norm.customer || {};
                      const pay = norm.payment || {};
                      const tr = ev.integration_trace || {};
                      const amt = tr.amount ?? norm.amount ?? pay.amount;
                      const line =
                        ev.event_type_normalized === "api_trigger"
                          ? tr.order_status
                            ? `Webhook + API · pedido ${tr.order_status}`
                            : "Webhook + consulta API"
                          : ev.event_type_normalized === "pix_paid"
                            ? "PIX pago"
                            : ev.event_type_normalized === "pix_expired"
                              ? "PIX expirado"
                              : "PIX criado";
                      const who =
                        tr.customer_name ||
                        tr.customer_phone ||
                        cust.name ||
                        cust.phone ||
                        ev.external_transaction_id ||
                        "—";
                      const origin =
                        ev.provider ||
                        (ev.normalized_payload?.mode === "api_connector" || ev.event_type_normalized === "api_trigger"
                          ? "API + webhook"
                          : ev.platform || "—");
                      const integLabel = ev.integration_id ? (
                        <span className="font-mono text-[10px] text-slate-500" title={ev.integration_id}>
                          {(ev.integration_id || "").slice(0, 8)}…
                        </span>
                      ) : (
                        "—"
                      );
                      const recLabel = tr.recovery_status || (tr.recovery_started ? "recovery_started" : "—");
                      return (
                        <tr key={ev.id} className="border-t border-white/[0.06] hover:bg-white/[0.02]">
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{ev.received_at || "—"}</td>
                          <td className="px-3 py-2">{integLabel}</td>
                          <td className="px-3 py-2 font-mono">{origin}</td>
                          <td className="px-3 py-2">{line}</td>
                          <td className="px-3 py-2 max-w-[140px] truncate" title={who}>
                            {who}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{amt != null ? formatBRL(amt) : "—"}</td>
                          <td className="px-3 py-2">
                            <span className="text-slate-300">{statusLabel(ev.status)}</span>
                            {ev.ignore_reason ? (
                              <span className="block text-amber-400/90 truncate max-w-[180px]" title={ev.ignore_reason}>
                                {ev.ignore_reason}
                              </span>
                            ) : null}
                            {ev.error_message ? (
                              <span className="block text-red-400/90 truncate max-w-[180px]" title={ev.error_message}>
                                {ev.error_message}
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 max-w-[120px] truncate text-slate-500" title={recLabel}>
                            {recLabel}
                          </td>
                          <td className="px-2 py-2">
                            <button
                              type="button"
                              onClick={() => openDetail(ev)}
                              className="p-1 rounded-sm hover:bg-white/10 text-cp-primary-light"
                              title="Detalhes"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </TabsContent>

        <TabsContent value="diagnostics" className="mt-0 space-y-6">
          <section className="rounded-sm border border-white/[0.08] bg-cp-bg/80 px-5 py-5 md:px-6 md:py-6">
            <p className="mb-5 max-w-2xl text-sm leading-relaxed text-slate-400">
              Veja se o último webhook trouxe o ID da venda, se a API respondeu e se há telefone e status para permitir recuperação de PIX.
            </p>
            {apiConnectors.length === 0 ? (
              <p className="text-sm text-slate-500">Crie uma integração na aba &quot;Webhook + API&quot; para ver diagnóstico aqui.</p>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-[10px] font-mono uppercase text-slate-500 mb-1">Integração</label>
                    <select
                      value={diagTabIntegrationId}
                      onChange={(e) => setDiagTabIntegrationId(e.target.value)}
                      className="bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 text-sm min-w-[200px]"
                    >
                      {apiConnectors.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={loadDiagTab}
                    className="inline-flex items-center gap-2 px-4 py-2 text-xs bg-cp-primary/20 border border-cp-primary/40 text-cp-primary-light rounded-sm hover:bg-cp-primary/30"
                  >
                    <Stethoscope className="h-3.5 w-3.5" />
                    Carregar diagnóstico
                  </button>
                </div>
                {diagTabLoading ? (
                  <p className="text-sm text-slate-500">Carregando…</p>
                ) : diagTabData ? (
                  <div className="space-y-4 text-sm">
                    {diagTabData.last_error_message ? (
                      <div className="text-xs text-red-300 border border-red-500/25 bg-red-500/5 rounded-sm px-3 py-2">
                        Último erro na integração: {diagTabData.last_error_message}
                      </div>
                    ) : null}
                    <ul className="space-y-1.5 text-xs">
                      {(diagTabData.checklist || []).map((row, i) => (
                        <li key={i} className={row.ok ? "text-emerald-400" : "text-amber-300"}>
                          {row.ok ? "✅" : "❌"} {row.label}
                        </li>
                      ))}
                    </ul>
                    <div className="text-xs text-slate-500">
                      Nível de compatibilidade (heurística):{" "}
                      <span className="text-cp-primary-light font-mono uppercase">{diagTabData.compatibility_tier}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => diagTabIntegrationId && openDiagnostics(diagTabIntegrationId)}
                      className="text-xs text-cp-primary-light underline"
                    >
                      Abrir diagnóstico em janela (detalhes extras)
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Clique em &quot;Carregar diagnóstico&quot; para ver o checklist desta integração.</p>
                )}
              </div>
            )}
          </section>
        </TabsContent>
      </Tabs>

      <AlertDialog
        open={regenerateWebhookConfirmId != null}
        onOpenChange={(open) => {
          if (!open) setRegenerateWebhookConfirmId(null);
        }}
      >
        <AlertDialogContent className="border border-white/10 bg-cp-bg text-slate-200 sm:rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-slate-100">Regenerar webhook?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-400">
                <p>Se você confirmar:</p>
                <ul className="list-disc space-y-1 pl-4 text-left">
                  <li>uma nova URL de webhook será gerada e mostrada aqui;</li>
                  <li>a URL antiga deixa de funcionar na hora — nada mais é enviado para ela;</li>
                  <li>é obrigatório colar a nova URL no checkout ou gateway (plataforma externa), senão os eventos não chegam mais nesta integração.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-slate-300 hover:bg-white/10 hover:text-slate-100">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-cp-primary text-white hover:bg-cp-primary-hover"
              onClick={() => {
                if (regenerateWebhookConfirmId != null) void regenerateConnectorWebhook(regenerateWebhookConfirmId);
              }}
            >
              Sim, regenerar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-cp-bg border border-white/10 text-slate-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Detalhe do evento</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <p className="text-sm text-slate-500">Carregando…</p>
          ) : detail?.event ? (
            <div className="space-y-4 text-xs">
              <div className="font-mono text-slate-400 space-y-1">
                <div>correlation_id: {detail.event.correlation_id}</div>
                <div>status: {detail.event.status}</div>
                {detail.event.provider ? <div>provider: {detail.event.provider}</div> : null}
                {detail.event.external_transaction_id ? (
                  <div>external_transaction_id: {detail.event.external_transaction_id}</div>
                ) : null}
                {detail.event.transaction_id ? <div>transaction_id: {detail.event.transaction_id}</div> : null}
                {detail.event.integration_id ? <div>integration_id: {detail.event.integration_id}</div> : null}
              </div>
              {detail.event.integration_trace ? (
                <div>
                  <div className="font-mono uppercase text-slate-500 mb-1">integration_trace (API / recuperação)</div>
                  <pre className="bg-cp-elevated border border-white/10 rounded-sm p-3 overflow-x-auto max-h-40 text-slate-400">
                    {JSON.stringify(detail.event.integration_trace, null, 2)}
                  </pre>
                </div>
              ) : null}
              {detail.transaction ? (
                <div>
                  <div className="font-mono uppercase text-slate-500 mb-1">Transação</div>
                  <pre className="bg-cp-elevated border border-white/10 rounded-sm p-3 overflow-x-auto max-h-48 text-slate-400">
                    {JSON.stringify(detail.transaction, null, 2)}
                  </pre>
                </div>
              ) : null}
              {detail.scheduled_jobs?.length ? (
                <div>
                  <div className="font-mono uppercase text-slate-500 mb-1">Jobs agendados (prévia)</div>
                  <pre className="bg-cp-elevated border border-white/10 rounded-sm p-3 overflow-x-auto max-h-48 text-slate-400">
                    {JSON.stringify(detail.scheduled_jobs, null, 2)}
                  </pre>
                </div>
              ) : null}
              {detail.contact ? (
                <div>
                  <div className="font-mono uppercase text-slate-500 mb-1">Contato</div>
                  <pre className="bg-cp-elevated border border-white/10 rounded-sm p-3 overflow-x-auto max-h-40 text-slate-400">
                    {JSON.stringify(detail.contact, null, 2)}
                  </pre>
                </div>
              ) : null}
              {detail.external_order_snapshot ? (
                <div>
                  <div className="font-mono uppercase text-slate-500 mb-1">Consulta API / snapshot do pedido</div>
                  <pre className="bg-cp-elevated border border-white/10 rounded-sm p-3 overflow-x-auto max-h-56 text-slate-400">
                    {JSON.stringify(detail.external_order_snapshot, null, 2)}
                  </pre>
                </div>
              ) : null}
              <div>
                <div className="font-mono uppercase text-slate-500 mb-1">raw_payload</div>
                <pre className="bg-cp-elevated border border-white/10 rounded-sm p-3 overflow-x-auto max-h-48">
                  {JSON.stringify(detail.event.raw_payload, null, 2)}
                </pre>
              </div>
              <div>
                <div className="font-mono uppercase text-slate-500 mb-1">normalized_payload</div>
                <pre className="bg-cp-elevated border border-white/10 rounded-sm p-3 overflow-x-auto max-h-48">
                  {JSON.stringify(detail.event.normalized_payload, null, 2)}
                </pre>
              </div>
              {detail.debug_logs?.length ? (
                <div>
                  <div className="font-mono uppercase text-slate-500 mb-1">Logs (Dev Center)</div>
                  <ul className="space-y-1 max-h-40 overflow-y-auto text-slate-400">
                    {detail.debug_logs.map((l) => (
                      <li key={l.id} className="border-b border-white/[0.04] pb-1">
                        <span className="text-cp-primary-light">{l.event_type}</span> — {l.status}{" "}
                        {l.error_detail ? <span className="text-amber-300">({l.error_detail})</span> : null}
                        {l.meta ? (
                          <pre className="mt-1 text-[10px] text-slate-500 whitespace-pre-wrap break-all max-h-24 overflow-y-auto">
                            {JSON.stringify(l.meta, null, 0)}
                          </pre>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {detail.ai_report ? (
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => copy(detail.ai_report)}
                    className="inline-flex items-center gap-2 self-start px-3 py-2 border border-cp-primary/40 text-cp-primary-light rounded-sm text-xs"
                  >
                    <ClipboardList className="h-3.5 w-3.5" />
                    Copiar relatório para IA
                  </button>
                  <pre className="bg-cp-elevated/50 border border-white/10 rounded-sm p-3 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                    {detail.ai_report}
                  </pre>
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={caktoOpen} onOpenChange={setCaktoOpen}>
        <DialogContent className="max-w-md bg-cp-bg border border-white/10 text-slate-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Conectar Cakto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-slate-500 leading-relaxed">
              Informe as credenciais da API pública da Cakto. Depois de salvar, copie a URL de webhook gerada e configure na Cakto. O segredo não
              será exibido novamente completo no painel.
            </p>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Nome da integração</label>
              <input
                value={caktoName}
                onChange={(e) => setCaktoName(e.target.value)}
                className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2"
                placeholder="Ex.: Cakto — loja principal"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Client ID</label>
              <input
                value={caktoClientId}
                onChange={(e) => setCaktoClientId(e.target.value)}
                className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Client Secret</label>
              <input
                value={caktoSecret}
                onChange={(e) => setCaktoSecret(e.target.value)}
                type="password"
                className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                autoComplete="new-password"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setCaktoOpen(false)} className="px-3 py-2 text-xs border border-white/10 rounded-sm">
                Cancelar
              </button>
              <button
                type="button"
                disabled={caktoSaving}
                onClick={submitCakto}
                className="px-4 py-2 text-xs bg-cp-primary/25 border border-cp-primary/50 rounded-sm text-cp-primary-light disabled:opacity-40"
              >
                {caktoSaving ? "Salvando…" : "Salvar e gerar webhook"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={syncpayOpen} onOpenChange={setSyncpayOpen}>
        <DialogContent className="max-w-md bg-cp-bg border border-white/10 text-slate-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Conectar Sync Pay</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-slate-500 leading-relaxed">
              Informe o Client ID e Client Secret da API partner (<span className="font-mono">api.syncpayments.com.br</span>). Depois de salvar, copie a URL de
              webhook e configure na Sync Pay. O segredo não será exibido completo no painel.
            </p>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Nome da integração</label>
              <input
                value={syncpayName}
                onChange={(e) => setSyncpayName(e.target.value)}
                className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2"
                placeholder="Ex.: Sync Pay — loja principal"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Client ID (chave pública)</label>
              <input
                value={syncpayClientId}
                onChange={(e) => setSyncpayClientId(e.target.value)}
                className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Client Secret (chave privada)</label>
              <input
                value={syncpaySecret}
                onChange={(e) => setSyncpaySecret(e.target.value)}
                type="password"
                className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                autoComplete="new-password"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setSyncpayOpen(false)} className="px-3 py-2 text-xs border border-white/10 rounded-sm">
                Cancelar
              </button>
              <button
                type="button"
                onClick={saveSyncpay}
                disabled={syncpaySaving}
                className="px-4 py-2 text-xs bg-cp-primary/25 border border-cp-primary/50 rounded-sm text-cp-primary-light disabled:opacity-40"
              >
                {syncpaySaving ? "Salvando…" : "Salvar e gerar webhook"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md bg-cp-bg border border-white/10 text-slate-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Editar integração</DialogTitle>
          </DialogHeader>
          {editRow ? (
            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Nome</label>
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2"
                />
              </div>
              {editRow.auth_type === "cakto_token" || editRow.auth_type === "syncpay_token" ? (
                <div className="space-y-2 border border-white/10 rounded-sm p-3 bg-cp-elevated/30">
                  <p className="text-xs text-slate-500">
                    Para trocar credenciais, informe Client ID e Client Secret completos (o painel não guarda o segredo em claro para exibir de
                    volta).
                  </p>
                  <input
                    value={editCaktoClientId}
                    onChange={(e) => setEditCaktoClientId(e.target.value)}
                    placeholder="Client ID (novo)"
                    className="w-full bg-cp-bg border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                    autoComplete="off"
                  />
                  <input
                    value={editSecret}
                    onChange={(e) => setEditSecret(e.target.value)}
                    type="password"
                    placeholder="Client Secret (novo)"
                    className="w-full bg-cp-bg border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                    autoComplete="new-password"
                  />
                </div>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditOpen(false)} className="px-3 py-2 text-xs border border-white/10 rounded-sm">
                  Fechar
                </button>
                <button
                  type="button"
                  disabled={editSaving}
                  onClick={submitEditConnector}
                  className="px-4 py-2 text-xs bg-cp-primary/25 border border-cp-primary/50 rounded-sm text-cp-primary-light disabled:opacity-40"
                >
                  {editSaving ? "Salvando…" : "Salvar"}
                </button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={testOrderOpen} onOpenChange={setTestOrderOpen}>
        <DialogContent className="max-w-md bg-cp-bg border border-white/10 text-slate-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Testar pedido (API)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-slate-500">Consulta a API com suas credenciais salvas e mostra os dados normalizados.</p>
            <input
              value={testOrderInput}
              onChange={(e) => setTestOrderInput(e.target.value)}
              placeholder={testOrderIdPlaceholder}
              className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
            />
            <button
              type="button"
              onClick={runTestOrderModal}
              className="w-full py-2 text-xs bg-cp-primary/25 border border-cp-primary/50 rounded-sm text-cp-primary-light"
            >
              Consultar pedido
            </button>
            {testOrderResult ? (
              <div className="border-t border-white/10 pt-3 space-y-2 text-xs">
                <ul className="space-y-1">
                  {(testOrderResult.checks || []).map((ch, i) => (
                    <li key={i} className={ch.ok ? "text-emerald-400" : "text-amber-300"}>
                      {ch.ok ? "✅" : "❌"} {ch.label}
                      {ch.detail ? ` — ${ch.detail}` : ""}
                    </li>
                  ))}
                </ul>
                {testOrderResult.normalized_order ? (
                  <pre className="bg-cp-elevated border border-white/10 rounded-sm p-2 max-h-40 overflow-auto text-slate-400">
                    {JSON.stringify(testOrderResult.normalized_order, null, 2)}
                  </pre>
                ) : null}
                {testOrderResult.correlation_id ? (
                  <p className="text-[10px] font-mono text-slate-500">correlation_id: {testOrderResult.correlation_id}</p>
                ) : null}
                {recoveryHintFromTestResult(testOrderResult) ? (
                  <div className="space-y-2 pt-2 border-t border-white/10">
                    <button
                      type="button"
                      disabled={testOrderRecovering}
                      onClick={runStartRecoveryFromTest}
                      className="w-full py-2 text-xs bg-emerald-600/30 border border-emerald-500/50 rounded-sm text-emerald-100 disabled:opacity-40"
                    >
                      {testOrderRecovering ? "Iniciando…" : "Iniciar recuperação para este pedido"}
                    </button>
                    <p className="text-[10px] text-slate-500 leading-relaxed">
                      Cria/atualiza transação e contato, agenda lembretes de recuperação (e campanha/fluxo PIX se configurados). Requer recuperação
                      ativa e WhatsApp conectado.
                    </p>
                  </div>
                ) : null}
                {testOrderRecoveryMsg ? (
                  <p className="text-xs text-cp-primary-light border border-cp-primary/30 rounded-sm px-2 py-1.5">{testOrderRecoveryMsg}</p>
                ) : null}
                {testOrderResult.error ? <p className="text-red-300">{testOrderResult.error}</p> : null}
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-cp-bg border border-white/10 text-slate-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Histórico da integração</DialogTitle>
            <p className="text-xs text-slate-500 font-mono">{histIntegName}</p>
            <p className="text-[10px] text-slate-500 leading-relaxed pt-1">
              Só entram linhas quando a plataforma envia POST para a URL de webhook <strong>desta</strong> integração.{" "}
              <strong className="text-slate-400">Cakto:</strong> app Webhooks + evento <span className="font-mono">pix_gerado</span> (ou outro com{" "}
              <span className="font-mono">id</span>/<span className="font-mono">refId</span>).{" "}
              <strong className="text-slate-400">Sync Pay:</strong> evento <strong>Recebimento - Cash in</strong> (não Cash out) e URL desta integração. Se aparecer{" "}
              <span className="font-mono">ignored:missing_phone</span>, o POST não trouxe telefone (ou não está em <span className="font-mono">data.client</span>
              / metadados) — a Sync Pay precisa enviar o telefone no fluxo de cash-in.
            </p>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <div className="flex flex-wrap gap-2">
              {(["all", "processed", "recovery_started", "pending", "paid", "ignored", "missing_phone", "failed"]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setHistFilter(k);
                    loadIntegrationHistory(histIntegId, k);
                  }}
                  className={`px-2 py-1 text-[10px] font-mono uppercase rounded-sm border ${
                    histFilter === k ? "bg-cp-primary/25 border-cp-primary/50 text-cp-primary-light" : "border-white/10 text-slate-500"
                  }`}
                >
                  {EVENT_FILTER_LABEL[k]}
                </button>
              ))}
            </div>
            {histLoading ? (
              <p className="text-slate-500">Carregando…</p>
            ) : histEvents.length === 0 ? (
              <div className="rounded-sm border border-white/[0.08] bg-cp-elevated/20 px-4 py-3 text-slate-400 space-y-2">
                <p className="text-sm text-slate-300">Nenhum evento para esta integração com o filtro atual.</p>
                <ul className="list-disc pl-4 space-y-1 text-[11px] leading-relaxed">
                  <li>
                    Use o filtro <strong className="text-slate-300">Todos</strong> ou <strong className="text-slate-300">Ignorados</strong> — falhas
                    de extração do ID aparecem como ignorados (<span className="font-mono">missing_external_transaction_id</span>).
                  </li>
                  <li>
                    URL correta: <span className="font-mono text-cp-primary-light/90">/api/integrations/webhook/…</span> desta integração (não o webhook genérico
                    da conta).
                  </li>
                  <li>
                    Sync Pay: <span className="font-mono">missing_phone</span> = webhook sem número válido (11+ dígitos com DDD); confira payload em detalhes do
                    evento.
                  </li>
                </ul>
              </div>
            ) : (
              <div className="overflow-x-auto border border-white/[0.06] rounded-sm">
                <table className="w-full text-left text-xs">
                  <thead className="bg-cp-elevated text-slate-500 font-mono uppercase">
                    <tr>
                      <th className="px-2 py-2">Quando</th>
                      <th className="px-2 py-2">Cliente / ref.</th>
                      <th className="px-2 py-2">Valor</th>
                      <th className="px-2 py-2">Pedido</th>
                      <th className="px-2 py-2">Recuperação</th>
                      <th className="px-2 py-2">Status</th>
                      <th className="px-2 py-2 w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {histEvents.map((ev) => {
                      const tr = ev.integration_trace || {};
                      const who = tr.customer_name || tr.customer_phone || ev.external_transaction_id || "—";
                      return (
                        <tr key={ev.id} className="border-t border-white/[0.06]">
                          <td className="px-2 py-2 text-slate-400 whitespace-nowrap">{ev.received_at || "—"}</td>
                          <td className="px-2 py-2 max-w-[140px] truncate" title={who}>
                            {who}
                          </td>
                          <td className="px-2 py-2">{tr.amount != null ? formatBRL(tr.amount) : "—"}</td>
                          <td className="px-2 py-2">{tr.order_status || "—"}</td>
                          <td className="px-2 py-2 max-w-[160px] truncate" title={tr.recovery_status || ""}>
                            {tr.recovery_status || "—"}
                          </td>
                          <td className="px-2 py-2">{statusLabel(ev.status)}</td>
                          <td className="px-2 py-2">
                            <button
                              type="button"
                              onClick={() => openDetail(ev)}
                              className="p-1 rounded-sm hover:bg-white/10 text-cp-primary-light"
                              title="Detalhes"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="max-w-lg bg-cp-bg border border-white/10 text-slate-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Nova integração via API</DialogTitle>
          </DialogHeader>
          {wizStep < 4 ? (
            <div className="space-y-4 text-sm">
              <div className="text-[10px] font-mono text-slate-500">Passo {wizStep + 1} de 4</div>
              {wizStep === 0 ? (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Nome da integração</label>
                  <input
                    value={wizName}
                    onChange={(e) => setWizName(e.target.value)}
                    className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2"
                    placeholder="Ex.: Checkout principal"
                  />
                </div>
              ) : null}
              {wizStep === 1 ? (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Tipo de autenticação</label>
                  <select
                    value={wizAuth}
                    onChange={(e) => setWizAuth(e.target.value)}
                    className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2"
                  >
                    <option value="api_key">Chave de API</option>
                    <option value="bearer_token">Bearer token</option>
                    <option value="oauth2_client_credentials">OAuth2 (client credentials)</option>
                    <option value="none">Nenhuma</option>
                  </select>
                </div>
              ) : null}
              {wizStep === 2 ? (
                <div className="space-y-3">
                  {wizAuth === "api_key" ? (
                    <>
                      <input
                        value={wizApiKey}
                        onChange={(e) => setWizApiKey(e.target.value)}
                        placeholder="API key"
                        className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                      />
                      <input
                        value={wizHeaderName}
                        onChange={(e) => setWizHeaderName(e.target.value)}
                        placeholder="Nome do header (opcional, ex.: X-API-Key)"
                        className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                      />
                    </>
                  ) : null}
                  {wizAuth === "bearer_token" ? (
                    <input
                      value={wizBearer}
                      onChange={(e) => setWizBearer(e.target.value)}
                      placeholder="Access token"
                      className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                    />
                  ) : null}
                  {wizAuth === "oauth2_client_credentials" ? (
                    <div className="space-y-2">
                      <input
                        value={wizOAuthUrl}
                        onChange={(e) => setWizOAuthUrl(e.target.value)}
                        placeholder="Token URL"
                        className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                      />
                      <input
                        value={wizOAuthId}
                        onChange={(e) => setWizOAuthId(e.target.value)}
                        placeholder="Client ID"
                        className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                      />
                      <input
                        value={wizOAuthSecret}
                        onChange={(e) => setWizOAuthSecret(e.target.value)}
                        placeholder="Client secret"
                        type="password"
                        className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                      />
                      <input
                        value={wizOAuthScope}
                        onChange={(e) => setWizOAuthScope(e.target.value)}
                        placeholder="Escopo (opcional)"
                        className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                      />
                    </div>
                  ) : null}
                  {wizAuth === "none" ? <p className="text-xs text-slate-500">Nenhuma credencial será enviada nos pedidos à API.</p> : null}
                </div>
              ) : null}
              {wizStep === 3 ? (
                <div className="space-y-3">
                  <input
                    value={wizBaseUrl}
                    onChange={(e) => setWizBaseUrl(e.target.value)}
                    placeholder="URL base da API (ex.: https://api.seudominio.com)"
                    className="w-full bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                  />
                  <div className="flex gap-2">
                    <select
                      value={wizMethod}
                      onChange={(e) => setWizMethod(e.target.value)}
                      className="bg-cp-elevated border border-white/10 rounded-sm px-2 py-2 text-xs"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                    </select>
                    <input
                      value={wizPath}
                      onChange={(e) => setWizPath(e.target.value)}
                      placeholder="/orders/{id}"
                      className="flex-1 bg-cp-elevated border border-white/10 rounded-sm px-3 py-2 font-mono text-xs"
                    />
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Use <code className="text-cp-primary-light">{"{id}"}</code> no caminho onde entra o ID da venda recebido no webhook.
                  </p>
                </div>
              ) : null}
              <div className="flex justify-between pt-2">
                <button
                  type="button"
                  disabled={wizStep === 0}
                  onClick={() => setWizStep((s) => Math.max(0, s - 1))}
                  className="text-xs text-slate-400 disabled:opacity-30"
                >
                  Voltar
                </button>
                {wizStep < 3 ? (
                  <button
                    type="button"
                    onClick={() => setWizStep((s) => s + 1)}
                    className="px-4 py-2 text-xs bg-cp-primary/25 border border-cp-primary/50 rounded-sm text-cp-primary-light"
                  >
                    Próximo
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={wizSaving || !wizName.trim() || !wizBaseUrl.trim()}
                    onClick={submitWizard}
                    className="px-4 py-2 text-xs bg-cp-primary/25 border border-cp-primary/50 rounded-sm text-cp-primary-light disabled:opacity-40"
                  >
                    {wizSaving ? "Salvando…" : "Criar integração"}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <p className="text-emerald-400 text-sm">Integração criada. Copie a URL do webhook no seu checkout.</p>
              <div>
                <div className="text-[10px] font-mono uppercase text-slate-500 mb-1">Webhook (URL simples)</div>
                <code className="block text-xs font-mono bg-cp-elevated border border-white/10 p-2 rounded-sm break-all">
                  {wizCreated?.webhook_url_token_only}
                </code>
                <button type="button" onClick={() => copy(wizCreated?.webhook_url_token_only)} className="mt-2 text-xs text-cp-primary-light underline">
                  Copiar
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Em seguida use &quot;Testar conexão&quot; e &quot;Testar webhook&quot; na lista para validar credenciais e o fluxo completo.
              </p>
              <button type="button" onClick={() => setWizardOpen(false)} className="w-full py-2 text-xs border border-white/10 rounded-sm">
                Fechar
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent className="max-w-lg bg-cp-bg border border-white/10 text-slate-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Diagnóstico</DialogTitle>
          </DialogHeader>
          {diagLoading ? (
            <p className="text-sm text-slate-500">Carregando…</p>
          ) : diagData ? (
            <div className="space-y-3 text-xs">
              {diagData.last_error_message ? (
                <div className="text-red-300 border border-red-500/20 rounded-sm px-2 py-1.5">Erro: {diagData.last_error_message}</div>
              ) : null}
              <div>
                Compatibilidade:{" "}
                <span className="text-cp-primary-light font-semibold uppercase">{diagData.compatibility_tier}</span>{" "}
                <span className="text-slate-500">(alta / média / baixa — heurística)</span>
              </div>
              <ul className="list-disc pl-4 text-slate-400 space-y-1">
                {(diagData.signals || []).map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
              {(diagData.checklist || []).length ? (
                <div>
                  <div className="font-mono uppercase text-slate-500 mb-1">Checklist</div>
                  <ul className="space-y-1">
                    {(diagData.checklist || []).map((row, i) => (
                      <li key={i} className={row.ok ? "text-emerald-400" : "text-amber-300"}>
                        {row.ok ? "✅" : "❌"} {row.label}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="text-slate-500">
                Recuperação pode iniciar (heurística):{" "}
                <span className={diagData.recovery_can_start_heuristic ? "text-emerald-400" : "text-amber-300"}>
                  {diagData.recovery_can_start_heuristic ? "sim" : "não"}
                </span>
              </div>
              <div className="font-mono text-[10px] text-slate-600">integration_id: {diagId}</div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

    </div>
  );
}




